import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronUp, faSearch, faTimes, faDownload, faBookmark } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';

interface Entity {
  id: number;
  label: string;
  location?: {
    box: [number, number, number, number];
    page: number;
  }[];
  probability: number;
  text: string;
}

interface ObjectValue {
  id: number;
  text: string;
  relation_score: number;
}

interface ObjectData {
  relation: string;
  values: {
    [key: string]: ObjectValue[];
  };
}

interface DisplayItem {
  type: 'object' | 'entity';
  data: ObjectData | Entity;
  formattedLabel: string;
  indexInGroup?: number;
  totalInGroup?: number;
}

@Component({
  selector: 'app-entities-view',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule],
  templateUrl: './entities-view.component.html',
  styleUrls: ['./entities-view.component.scss']
})
export class EntitiesViewComponent implements OnChanges {
  @Input() entities: Entity[] = [];
  @Input() objects: ObjectData[] = [];
  @Input() selectedEntityId: number | null = null;
  @Output() entityClick = new EventEmitter<number>();
  @Output() download = new EventEmitter<void>();
  @Output() saveReference = new EventEmitter<void>();
  @Output() contentChanged = new EventEmitter<void>();
  @Input() canSaveReference: boolean = true;
  @Input() showDownloadButton: boolean = true;
  @Input() showSaveReferenceButton: boolean = true;
  @Input() showConfidence: boolean = true;

  filterText: string = '';
  filteredItems: DisplayItem[] = [];
  displayItems: DisplayItem[] = [];

  objectCount: number = 0;
  
  icons = {
    faChevronDown,
    faChevronUp,
    faSearch,
    faTimes,
    faDownload,
    faBookmark
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entities'] || changes['objects']) {
      this.buildDisplayItems();
    }
    if (changes['selectedEntityId'] && this.selectedEntityId !== null) {
      this.scrollToEntity(this.selectedEntityId);
    }
  }

  scrollToEntity(entityId: number): void {
    setTimeout(() => {
      const element = document.getElementById(`entity-item-${entityId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  buildDisplayItems(): void {
    this.displayItems = [];

    const objectsList = this.objects || [];
    const relationTotals: { [key: string]: number } = {};
    const relationCounts: { [key: string]: number } = {};

    objectsList.forEach(obj => {
      relationTotals[obj.relation] = (relationTotals[obj.relation] || 0) + 1;
    });

    objectsList.forEach(obj => {
      const rel = obj.relation;
      relationCounts[rel] = (relationCounts[rel] || 0) + 1;
      this.displayItems.push({
        type: 'object',
        data: obj,
        formattedLabel: this.formatLabel(obj.relation),
        indexInGroup: relationCounts[rel],
        totalInGroup: relationTotals[rel]
      });
    });

    const relatedEntityIds = this.getRelatedIds();
    const entitiesList = this.entities || [];
    const labelTotals: { [key: string]: number } = {};
    const labelCounts: { [key: string]: number } = {};

    const filteredEntities = entitiesList.filter(entity => 
      !relatedEntityIds.has(entity.id) && !relatedEntityIds.has(String(entity.id))
    );

    filteredEntities.forEach(entity => {
      labelTotals[entity.label] = (labelTotals[entity.label] || 0) + 1;
    });

    filteredEntities.forEach(entity => {
      const lbl = entity.label;
      labelCounts[lbl] = (labelCounts[lbl] || 0) + 1;
      this.displayItems.push({
        type: 'entity',
        data: entity,
        formattedLabel: this.formatLabel(entity.label),
        indexInGroup: labelCounts[lbl],
        totalInGroup: labelTotals[lbl]
      });
    });

    this.applyFilter();
  }

  getRelatedIds(): Set<any> {
    const ids = new Set<any>();
    (this.objects || []).forEach(obj => {
      const vals = obj.values || {};
      Object.keys(vals).forEach(key => {
        const arr = vals[key];
        if (Array.isArray(arr)) {
          arr.forEach(v => {
            if (v.id !== undefined && v.id !== null) {
              ids.add(v.id);
              ids.add(String(v.id));
            }
          });
        }
      });
    });
    return ids;
  }

  applyFilter(): void {
    if (!this.filterText) {
      this.filteredItems = [...this.displayItems];
      return;
    }

    const search = this.filterText.toLowerCase();
    this.filteredItems = this.displayItems.filter(item => {
      if (item.type === 'entity') {
        const entity = this.asEntity(item.data);
        return (
          entity.label.toLowerCase().includes(search) ||
          entity.text.toLowerCase().includes(search)
        );
      } else {
        const obj = this.asObject(item.data);
        const matchesRelation = obj.relation.toLowerCase().includes(search);
        if (matchesRelation) return true;

        return Object.keys(obj.values || {}).some(key => {
          if (key.toLowerCase().includes(search)) return true;
          return obj.values[key].some(v => v.text.toLowerCase().includes(search));
        });
      }
    });
  }

  onFilterChange(value: string): void {
    this.filterText = value;
    this.applyFilter();
  }

  clearFilter(): void {
    this.filterText = '';
    this.applyFilter();
  }

  onEntityClick(entityId: number): void {
    this.entityClick.emit(entityId);
  }

  isEntitySelected(entityId: number): boolean {
    return this.selectedEntityId === entityId;
  }

  formatLabel(label: string): string {
    if (!label) return '';
    return label
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getObjectKeys(values: any): string[] {
    return Object.keys(values || {});
  }

  getConfidenceColor(probability: number): string {
    if (probability >= 0.8) return '#4caf50';
    if (probability >= 0.6) return '#ff9800';
    return '#f44336';
  }

  getProbabilityPercent(probability: number | undefined | null): string {
    if (probability === undefined || probability === null || isNaN(probability)) {
      return '0';
    }
    return (probability * 100).toFixed(0);
  }

  asObject(data: any): ObjectData {
    return data as ObjectData;
  }

  asEntity(data: any): Entity {
    return data as Entity;
  }

  trackByItem(index: number, item: DisplayItem): string {
    if (item.type === 'entity') {
      return `entity-${(item.data as Entity).id}`;
    }
    return `object-${(item.data as ObjectData).relation}-${item.indexInGroup}`;
  }

  trackByValue(index: number, value: ObjectValue): number {
    return value.id;
  }

  onContentChanged(): void {
    this.contentChanged.emit();
  }
}
